<template>
    <p class="eyebrow" :class="mergedClass">
        <USkeleton v-if="loading" :class="mergedSkeletonClass" />
        <!--
          Keep copy mounted under the skeleton so opacity can ease in when `loading`
          clears (same brief fade as order-success hero / status card headers).
        -->
        <span
            class="transition-opacity translate-z-0 ease-out motion-reduce:transition-none"
            :class="contentOpacityClass"
            :style="contentRevealStyle"
            :aria-hidden="loading"
        >
            <slot />
        </span>
    </p>
</template>

<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { mergeUiClassMap } from '@/utils/defuStrConcat'
import { useOrderSuccessMotionConfig } from '~~/layers/base/composables/useOrderSuccessMotionConfig'

export type EyebrowUi = {
    /** Root class string — margin, color, and other contextual utilities. */
    base?: string
    skeleton?: string
}

interface EyebrowProps {
    /**
     * Navy / gradient surfaces: white eyebrow per DS `.on-dark .eyebrow`.
     * Default (false): gold per `.eyebrow`. Applied before `ui.base` so callers can still override color.
     */
    onDark?: boolean
    /**
     * Optional partial UI map (Nuxt UI / shared-wrapper pattern).
     * Merged after DS layout/color defaults via `twMerge` so callers can replace margin/color
     * without `!important` (those tokens intentionally live here, not in scoped CSS).
     */
    ui?: Partial<EyebrowUi>
    /**
     * When true, shows a loading skeleton over the eyebrow and keeps the slot content at
     * opacity 0. When cleared, content fades in briefly instead of snapping visible.
     */
    loading?: boolean
}

const props = withDefaults(defineProps<EyebrowProps>(), {
    onDark: false,
    ui: () => ({}),
    loading: false,
})

const { loading } = toRefs(props)

const orderSuccessMotion = useOrderSuccessMotionConfig()

/** Live copy fades in after the skeleton unmounts (matches order-status / success hero). */
const contentOpacityClass = computed(() => (loading.value ? 'opacity-0' : 'opacity-100'))

/** Duration for the content opacity fade when `loading` flips. */
const contentRevealStyle = computed(() => ({
    transitionDuration: `${orderSuccessMotion.skeletonFadeMs}ms`,
}))

/**
 * Default layout + brand color for the root. Color/margin stay out of scoped CSS so
 * `ui.base` can override them cleanly (e.g. `mb-1 text-(--neutral-500)`).
 */
const defaultBaseClass = computed(() =>
    twMerge(
        'relative m-0 text-(--gold) leading-(--leading-body)',
        props.onDark ? 'text-(--white)' : ''
    )
)

/** Default skeleton classes: width, height, and max-width. */
const defaultSkeletonClass = 'absolute w-full h-4 top-0.5'

/** Final root classes: defaults (+ onDark) then `ui.base` overrides via `twMerge`. */
const mergedClass = computed(() => {
    const merged = mergeUiClassMap({ base: defaultBaseClass.value }, props.ui ?? {})
    return typeof merged.base === 'string' ? merged.base : defaultBaseClass.value
})

/** Final skeleton classes: defaults then `ui.skeleton` overrides via `twMerge`. */
const mergedSkeletonClass = computed(() => twMerge(defaultSkeletonClass, props.ui?.skeleton ?? ''))
</script>

<style scoped>
/**
 * Canonical DS eyebrow recipe — mirrors `.eyebrow` in colors_and_type.css (not legacy
 * `.crane-eyebrow` / `--text-eyebrow` 10px).
 *
 * Color and margin are owned by the `ui` map (`text-(--gold)` / `m-0` by default) so parents
 * can override without Tailwind `!important`.
 */
.eyebrow {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    line-height: var(--leading-tight);
    text-transform: uppercase;
    letter-spacing: var(--tracking-eyebrow);
}
</style>
